import { SystemSemanticTagId } from "@redotech/redo-content-builder-common/forge/review-forge-types";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { object } from "zod";
import {
  ReviewFieldSchemaType,
  ReviewRating,
  numberToReviewRating,
} from "./review";
import {
  IReview,
  ReviewDbParser,
  ReviewReplyDbParser,
} from "./review-db-parser";

export const reviewBodySchema = ReviewDbParser.extend({
  _id: z.string(),
  teamId: z.string(),
  rating: z.optional(createEnum(ReviewRating)),
  productName: z.string().optional(),
  productDescription: z.string().optional(),
  productImageSrc: z.string().optional(),
});

export type ReviewBody = z.infer<typeof reviewBodySchema>;

export function reviewToReviewBodySchema(review: IReview): ReviewBody {
  return { ...review, rating: numberToReviewRating(review.rating) };
}

// This is the review body schema sent to the PDP widget, it makes unsafe
// assumptions about the meaning of the review fields since this is built
// before we have data tags to assign real meaning to the review fields.
export const reviewBodySchemaWithAdditionalInferredInfo = object({
  customerName: z.string(),
  createdAt: z.date(),
  verifiedPurchase: z.boolean(),
  shortAnswer: z.string().optional(),
  paragraph: z.string().optional(),
  rating: createEnum(ReviewRating),
  images: z.array(z.string()).optional(),
  replies: z.array(ReviewReplyDbParser).optional(),
  productName: z.string().optional(),
  productDescription: z.string().optional(),
  productImageSrc: z.string().optional(),
  variantTitle: z.string().optional(),
  variantImageSrc: z.string().optional(),
});

export type ReviewBodyWithAdditionalInferredInfo = z.infer<
  typeof reviewBodySchemaWithAdditionalInferredInfo
>;

export function reviewToReviewBodySchemaWithAdditionalInferredInfo(
  review: IReview,
): ReviewBodyWithAdditionalInferredInfo {
  const customerName = review.customer?.firstName
    ? `${review.customer.firstName}${review.customer.lastName ? ` ${review.customer.lastName.slice(0, 1)}.` : ""}`
    : "Anonymous";

  const nonEmailFields = review.reviewFields.filter(
    (field) => field.semanticTagId !== SystemSemanticTagId.REVIEW_EMAIL,
  );

  return {
    verifiedPurchase: review.verifiedPurchase,
    rating: numberToReviewRating(review.rating),
    customerName,
    createdAt: review.createdAt,
    shortAnswer: nonEmailFields.find(
      (field) => field.fieldType === ReviewFieldSchemaType.REVIEW_SHORT_ANSWER,
    )?.text,
    paragraph: nonEmailFields.find(
      (field) => field.fieldType === ReviewFieldSchemaType.REVIEW_PARAGRAPH,
    )?.text,
    images: review.reviewFields
      .filter(
        (field) =>
          field.fieldType === ReviewFieldSchemaType.REVIEW_MEDIA_UPLOAD,
      )
      ?.map((field) => field.text),
    replies: review.replies || [],
  };
}
